import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Formik } from 'formik';
import { Alert, Box, CircularProgress, Grid, Snackbar, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';

import { obterFormularioAtualJurado, salvarCadastroJurado } from '../../../api/jurados';
import { extrairMensagemErro } from '../../../utils/apiError';
import { CpfValido } from '../../../utils/validaDocumento';
import AplicarForcarValorSe from './AplicarForcarValorSe';
import CampoCadastro from './CampoCadastro';
import useAutoSave from './useAutoSave';
import useCamposFaltando from './useCamposFaltando';
import {
  faltaAceiteObrigatorio,
  ocupaLinhaInteira,
  prepararCampos,
  serializarValores,
  valoresIniciais,
} from './regrasCadastro';

/**
 * Cadastro do jurado no formulário configurado pelo cliente. Salva rascunho a cada
 * mudança (status "Pendente") e envia "Cadastrado" no botão final (só no wizard; no
 * perfil o rascunho automático basta). Quem veio por convite manda o id do convite junto.
 */
export default function FormularioJurado({ onSuccess, wizardMode = false, initialEmail = '', conviteJuradoId = null }) {
  const { t } = useTranslation();
  const conviteId = conviteJuradoId || sessionStorage.getItem('konq.conviteJuradoId') || null;

  const [formulario, setFormulario] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [falhaAoCarregar, setFalhaAoCarregar] = useState(false);
  const [aviso, setAviso] = useState(null); // { severity, message } do Snackbar

  useEffect(() => {
    obterFormularioAtualJurado()
      .then((res) => {
        const dados = res.data?.data ?? res.data;
        setFormulario({ ...dados, campos: prepararCampos(dados?.campos) });
      })
      .catch(() => setFalhaAoCarregar(true))
      .finally(() => setCarregando(false));
  }, []);

  const enviar = (valores, status) =>
    salvarCadastroJurado({
      versaoFormulario: formulario.versao ?? 1,
      status,
      valores: serializarValores(formulario.campos, valores),
      ...(conviteId ? { conviteJuradoId: parseInt(conviteId, 10) } : {}),
    });

  const rascunho = useAutoSave((valores) => enviar(valores, 'Pendente'));
  const faltantes = useCamposFaltando(formulario?.campos ?? []);

  const iniciais = useMemo(
    () => (formulario ? valoresIniciais(formulario.campos, formulario.valores, initialEmail) : {}),
    [formulario, initialEmail],
  );

  const concluir = async (valores, { setSubmitting, setStatus }) => {
    rascunho.cancelar();
    try {
      await enviar(valores, 'Cadastrado');
      setStatus(null);
      setAviso({ severity: 'success', message: t('validacao.cadastroSalvo') });
      onSuccess?.();
    } catch (erro) {
      const mensagem = faltantes.registrar(erro) ?? extrairMensagemErro(erro, t('Error saving changes.'));
      setStatus({ erro: mensagem });
      setAviso({ severity: 'error', message: mensagem });
    } finally {
      setSubmitting(false);
    }
  };

  if (carregando) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (falhaAoCarregar) return <Alert severity="error">{t('wizard.jurado.erroCarregarFormulario')}</Alert>;
  if (!formulario?.campos.length)
    return <Alert severity="info">{t('No fields configured in the form of this subcategory.')}</Alert>;

  return (
    <>
      <Helmet title={t('Judge Registration')} />
      <Snackbar
        open={Boolean(aviso)}
        autoHideDuration={5000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAviso(null)} severity={aviso?.severity} variant="filled" sx={{ width: '100%' }}>
          {aviso?.message}
        </Alert>
      </Snackbar>

      <Formik initialValues={iniciais} onSubmit={concluir} enableReinitialize>
        {({ values, setFieldValue, handleSubmit, isSubmitting, status }) => {
          const alterar = (mudancas) => {
            faltantes.limpar(Object.keys(mudancas));
            Object.entries(mudancas).forEach(([codigo, valor]) => setFieldValue(codigo, valor));
          };
          const alterarESalvar = (mudancas) => {
            alterar(mudancas);
            rascunho.agendar({ ...values, ...mudancas });
          };
          // Só valida o dígito do CPF aqui; a checagem de duplicidade fica com o backend.
          const digitosCpf = String(values.cpf ?? '').replace(/\D/g, '');
          const erroCpf = digitosCpf.length === 11 && !CpfValido(digitosCpf) ? t('validacao.cpfInvalido') : null;

          return (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <AplicarForcarValorSe campos={formulario.campos} valores={values} setFieldValue={setFieldValue} />
              <Stack spacing={2.5}>
                {status?.erro && <Alert severity="error">{status.erro}</Alert>}

                <Box>
                  {/* Box: o Stack zera a margem dos filhos e anularia a margem negativa do Grid */}
                  <Grid container spacing={2.5}>
                    {formulario.campos.map((campo) => {
                      const elemento = (
                        <CampoCadastro
                          campo={campo}
                          valores={values}
                          valoresSalvos={formulario.valores}
                          alterar={alterar}
                          alterarESalvar={alterarESalvar}
                          aoSair={() => rascunho.agendar(values)}
                          erro={(campo.codigo === 'cpf' && erroCpf) || faltantes.erroDe(campo.codigo)}
                          emailFixo={Boolean(initialEmail)}
                          logradouroSemNome="Zona Rural"
                        />
                      );
                      return (
                        <Grid
                          item
                          xs={12}
                          md={ocupaLinhaInteira(campo) ? 12 : 6}
                          key={campo.codigo}
                          sx={{ '&:empty': { display: 'none' } }}
                        >
                          {elemento}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {rascunho.falhou && (
                  <Typography variant="caption" color="error.main" textAlign="right">
                    {t('Error saving changes.')}
                  </Typography>
                )}

                {wizardMode && (
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    loading={isSubmitting}
                    startIcon={<Save />}
                    disabled={faltaAceiteObrigatorio(formulario.campos, values)}
                    sx={{ height: 48 }}
                  >
                    {t('Register')}
                  </LoadingButton>
                )}
              </Stack>
            </Box>
          );
        }}
      </Formik>
    </>
  );
}
